import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Papa, { ParseError } from 'papaparse';
import moment from 'moment';

// Material UI
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import CloseIcon from '@material-ui/icons/Close';

// Data
import { STEPS } from './importer.constants';
import { IImportColumn, IImportColumnSet, IImporterContainer, ImportRow } from './importer.interface';
import { confirmAllColumnMappings, padRowsToHeaderWidth, refreshMappedColumns } from './importer.helpers';

// Components
import { ImporterInstructions } from './importer-instructions.component';
import { ImportMatcherContainer } from './ImportColumnMatcher/import-matcher.container';
import { ImporterProgressStatus } from './importer-progress-status.component';
import { ImportHeaderSelection } from './importer-header-selection.component';
import { ImporterResults } from './importer-results.component';
import { IColumnMap } from './ImportColumnMatcher/import-matcher.interface';
import { SecondaryButton } from '../_ui/buttons.component';
import { Spacer } from '../_ui/structure.components';

// Stable defaults for the optional props: a `= []` default allocates a fresh array every render,
// which would churn the expectedColumns memo for any caller that omits them.
const NO_COLUMNS: IImportColumn[] = [];
const NO_COLUMN_SETS: IImportColumnSet[] = [];

export const ImporterContainer: React.FC<IImporterContainer> = ({
  step,
  setStep,
  importItems,
  setImportItems,
  importErrors,
  setImportErrors,
  requiredColumns = NO_COLUMNS,
  optionalColumns = NO_COLUMNS,
  repeatedColumnSets = NO_COLUMN_SETS,
  extensions = ['csv'],
  instructionHeader,
  instructionFooter,
  columnSelectionOptions,
  dataReviewSummary,
  showFileStructure,
  onSubmit,
  onReset,
  onMappedColumnsChange
}) => {
  const [parseError, setParseError] = useState<undefined|string>();
  const [fileName, setFileName] = useState<undefined|string>();
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [columnMap, setColumnMap] = useState<IColumnMap[]>([]);

  useEffect(() => {
    if (!onMappedColumnsChange) return;
    onMappedColumnsChange(_.compact(columnMap.map(mapping => (mapping.isIgnored ? null : mapping.column?.key))));
  }, [columnMap, onMappedColumnsChange]);

  // The step and the parsed rows outlive this component, so they are cleared explicitly wherever
  // the importer goes away; the local state above dies with it.
  const clearRetainedState = () => {
    setImportItems([]);
    setImportErrors([]);
    setStep(STEPS.INSTRUCTIONS);

    if (onReset) onReset();
  };

  const resetImport = () => {
    setColumnMap([]);
    setParseError(undefined);
    setFileName(undefined);
    setHeaderRowIndex(0);
    clearRetainedState();
  };

  // Read through a ref so the unmount cleanup below doesn't re-run every time a consumer hands
  // down a fresh setter identity.
  const clearRetainedStateRef = useRef(clearRetainedState);
  clearRetainedStateRef.current = clearRetainedState;

  // Nothing survives the panel closing: a second import in one session opens exactly as the
  // first did after a reload.
  useEffect(() => () => clearRetainedStateRef.current(), []);

  const handleReceiveFiles = (files: File[]) => {
    const currImportItems: ImportRow[] = [];
    let currImportErrors: ParseError[] = [];

    setStep(STEPS.PROCESSING_STATUS);
    setImportItems([]);
    setImportErrors([]);

    if (files.length) {
      setFileName(files[0].name);

      Papa.parse(files[0], {
        encoding: 'utf-8',
        step: (results: any, parser: any) => {
          const nextResult: string[] = _.map(results.data, column => String(column));

          currImportItems.push(nextResult);
          currImportErrors = currImportErrors.concat(results.errors);
        },
        complete: () => {
          const lastItem = currImportItems[currImportItems.length - 1];

          if ((lastItem.length === 1 && lastItem[0] === '') || lastItem.length === 0) {
            currImportItems.pop();
          }

          setImportItems(currImportItems);
          setImportErrors(currImportErrors);
          setStep(STEPS.HEADER_CHECK);
        }
      });
    } else {
      setParseError('')
    }
  };

  const handleImportSubmission = (invalidRowMap: ({ [key: string]: boolean })) => {
    const validImportItems = _.filter(importItems, (item, index) => index !== headerRowIndex && !invalidRowMap[index]);
    const collection = validImportItems.map(item => {
      const document: any = {};
      const repeatedSetKeys = [];
      const setKeyCount: { [key: string]: number } = {};

      for (let i = 0; i < repeatedColumnSets.length; i += 1) {
        document[repeatedColumnSets[i].key] = [];
        repeatedColumnSets[i].columns.forEach(column => repeatedSetKeys.push(column.key));
      }

      for (let i = 0; i < columnMap.length; i += 1) {
        const currColumnMap = columnMap[i];
        const currColumn = currColumnMap.column;

        if (!currColumnMap.isIgnored && currColumnMap.isLocked && currColumn) {
          const { key, set, format, formatType, importFormat } = currColumn;
          // A blank cell is an absent field, not an empty string: '' fails an optional enum the
          // server accepts unset, and a blank date formats to the string 'Invalid date'.
          const value = formatType === 'DATE' && item[i]
            ? moment(item[i], format).format(importFormat)
            : item[i] || undefined;

          if (set) {
            document[set] = document[set] || [];
            setKeyCount[key] = setKeyCount[key] ? setKeyCount[key] + 1 : 1;
            document[set][setKeyCount[key] - 1] = document[set][setKeyCount[key] - 1] || {};
            document[set][setKeyCount[key] - 1][key] = value;
          } else {
            document[key] = value;
          }
        }
      }

      return document;
    });

    // Keys are exactly the flagged data rows — the review step never flags the header row.
    onSubmit(collection, { skippedRowCount: Object.keys(invalidRowMap).length });
    // `collection` is the caller's from here, so the form clears without waiting on the submission.
    resetImport();
  };

  // Read inside ImportMatcherContainer's pre-map effect, so an unstable identity re-runs the
  // whole header-matching pass on every parent render.
  const expectedColumns = useMemo(
    () => requiredColumns
      .concat(optionalColumns)
      .concat(_.flatten(repeatedColumnSets.map(set => set.columns))),
    [requiredColumns, optionalColumns, repeatedColumnSets]
  );

  // A consumer can redefine a field while the operator is mid-flow — a validator that now accepts
  // a blank, say — and the map holds definitions, so they are brought up to date in place.
  useEffect(
    () => setColumnMap((current: IColumnMap[]) => refreshMappedColumns(current, expectedColumns)),
    [expectedColumns]
  );

  // Boundary guard: anything still unconfirmed locks on the way to review, so the review table,
  // findUnmatchedColumns, and handleImportSubmission cannot disagree about what imports. The
  // functional update is load-bearing — the dialog's discard runs in the same tick.
  const completeMatching = useCallback(() => {
    setColumnMap((current: IColumnMap[]) => confirmAllColumnMappings(current));
    setStep(STEPS.DATA_REVIEW);
  }, [setStep]);

  // Padding at the header boundary keeps every later step — matcher, review table, submission —
  // indexing rows by the same column positions a trimmed export would otherwise leave short.
  const confirmHeaderSelection = useCallback(() => {
    setImportItems(padRowsToHeaderWidth(importItems, headerRowIndex));
    setStep(STEPS.COLUMN_MATCHING);
  }, [importItems, headerRowIndex, setImportItems, setStep]);

  let content;

  switch (step) {
    case STEPS.INSTRUCTIONS:
      content = <ImporterInstructions
        extensions={extensions}
        handleReceivedFiles={handleReceiveFiles}
        parseError={parseError}
        header={instructionHeader}
        footer={instructionFooter}
        requiredColumns={requiredColumns}
        optionalColumns={optionalColumns}
        repeatedColumnSets={repeatedColumnSets}
        showFileStructure={showFileStructure}
      />;
      break;
    case STEPS.PROCESSING_STATUS:
      content =  <ImporterProgressStatus />;
      break;
    case STEPS.HEADER_CHECK:
      content = <ImportHeaderSelection
        fileName={fileName || 'loading'}
        rows={importItems}
        headerRowIndex={headerRowIndex}
        setHeaderRowIndex={setHeaderRowIndex}
        confirmSelection={confirmHeaderSelection}
      />;
      break;

    case STEPS.COLUMN_MATCHING:
      content = <ImportMatcherContainer
        requiredColumns={requiredColumns}
        expectedColumns={expectedColumns}
        rows={importItems}
        headerRowIndex={headerRowIndex}
        columnMap={columnMap}
        setColumnMap={setColumnMap}
        completeMatching={completeMatching}
        columnSelectionOptions={columnSelectionOptions}
      />;
      break;
    case STEPS.DATA_REVIEW:
      content = <ImporterResults
        rows={importItems}
        columnMap={columnMap}
        requiredColumns={requiredColumns}
        headerRowIndex={headerRowIndex}
        canEdit
        onUpdateRows={setImportItems}
        completeReview={handleImportSubmission}
        reviewSummary={dataReviewSummary}
      />;
      break;
    default:
      setStep(STEPS.INSTRUCTIONS);
      content = <Typography>Loading...</Typography>;
  }

  const previousStep = (currStep: number) => {
    switch(currStep) {
      case STEPS.HEADER_CHECK:
        setHeaderRowIndex(0);
        setImportItems([]);
        setImportErrors([]);
        setStep(STEPS.INSTRUCTIONS);
        break
      case STEPS.PROCESSING_STATUS:
        setImportItems([]);
        setImportErrors([]);
        setStep(STEPS.INSTRUCTIONS);
        break;
      case STEPS.COLUMN_MATCHING:
        setColumnMap([]);
        setStep(currStep - 1);
        break
      default:
        setStep(currStep - 1);
        // Do nothing...
    }
  };

  return (
    <Box>
      {step > 1 && <Box py={2} display="flex">
        <SecondaryButton
          onClick={() => previousStep(step)}
          startIcon={<ArrowBackIosIcon />}
        >Previous Step</SecondaryButton>
        <Spacer />
        <SecondaryButton
          onClick={() => resetImport()}
          startIcon={<CloseIcon />}
        >Cancel Import</SecondaryButton>
      </Box>}
      {content}
    </Box>
  );
}
